import { createApp } from 'vue'
import Admin from './Admin.vue'
import { createRouter, createWebHistory } from 'vue-router'
import vuetify from './plugins/vuetify'
import { loadFonts } from './plugins/webfontloader'
import mdiVue from 'mdi-vue/v3'
import * as mdijs from '@mdi/js'
import { createMetaManager, plugin as metaPlugin } from 'vue-meta'
import VueLogger from 'vuejs3-logger'
import { createAuthPlugin, authGuard, logoutUrlFor, resolveAuthProvider } from './utils/auth/index.js'
import VNetworkGraph from 'v-network-graph'
import 'v-network-graph/lib/style.css'
import emitter from './components/vue3-beautiful-chat/emitter'
// import { bindQueryParamsToProps } from './utils/authUtils.js'

const isProd = process.env.VUE_APP_ORIGIN?.startsWith('https://')

const loggerOpts = {
  isEnabled: true,
  logLevel: isProd ? 'error' : 'debug',
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: true,
  separator: '|',
  showConsoleColors: true,
}

loadFonts()

const app = createApp(Admin)

// vue3-beautiful-chat
app.config.globalProperties.$event = emitter

// Global error logger
app.config.errorHandler = (err, instance, info) => {
  console.log(`Uncaught error: ${err}`)
}

// Auth0 error interceptor — prevent login loop when access is denied.
// Auth0 redirects back with ?error=access_denied when an Action denies login.
// Without this guard, authGuard would redirect to Auth0 again → infinite loop.
const auth0ErrorMessages = {
  not_authorized: 'Your account has not been granted access to this application. Contact your ChaosTrack administrator.',
  no_org: 'Your account is not associated with an organization. Contact your ChaosTrack administrator.',
  server_error: 'An error occurred during login. Please try again or contact your ChaosTrack administrator.',
}

const auth0ErrorGuard = (to, from, next) => {
  const error = to.query.error
  const errorDesc = to.query.error_description
  if (error === 'access_denied' || error === 'unauthorized') {
    const friendlyMessage = auth0ErrorMessages[errorDesc] || errorDesc || 'Access denied. Contact your ChaosTrack administrator.'
    // Store error in sessionStorage so it survives the logout redirect
    sessionStorage.setItem('ct_auth_error', JSON.stringify({ error: 'Access Denied', description: friendlyMessage }))
    // Clear Auth0 session — redirect to a clean returnTo URL (no query params)
    // End-session URL shape differs per provider (Auth0 /v2/logout vs the OIDC
    // RP-initiated logout endpoint), so it is built by the auth facade.
    const returnTo = `${window.location.origin}/error`
    window.location.replace(logoutUrlFor(returnTo))
    return
  }
  next()
}

const router = createRouter({
  routes: [
    {
      path: '/',
      name: 'Splash',
      component: Admin,
    },
    {
      path: '/error',
      name: 'Error',
      component: Admin,
    },
    {
      path: '/admin',
      name: 'Admin',
      component: Admin,
      beforeEnter: [auth0ErrorGuard, authGuard],
    },
    {
      path: '/t/:navSession',
      name: 'Scenario',
      component: Admin,
      props: true,
      beforeEnter: [auth0ErrorGuard, authGuard],
    },
  ],
  history: createWebHistory(),
})

// The active sign-in provider is decided at runtime (Runtime Config → auth.provider), so resolve
// it before installing the auth plugin. Bounded (3 s) and never throws.
resolveAuthProvider().finally(() => {
  app
    .use(router)
    .use(vuetify)
    .use(createMetaManager())
    .use(metaPlugin)
    .use(mdiVue, {
      icons: mdijs,
    })
    .use(VueLogger, loggerOpts)
    .use(createAuthPlugin())
    .use(VNetworkGraph)
    .mount('#app')
})
